import React from 'react';

/* ====================================================================
   DigiHR Superadmin design tokens + shared primitives.

   Ink #10132B (sidebar/headline) · Signal #4C6FFF (primary/links)
   Amber #F5A524 (attention/urgency) · Emerald #16A34A (finance/healthy)
   Violet #7C5CFC (system) · Page #F5F6FA · Card #FFF · Border #E8EAF1
   Type: Inter (UI) + Roboto Mono (numbers/IDs/timestamps)

   Custom colors are applied via inline `style` rather than Tailwind
   arbitrary values so the palette lives in one place and can be
   re-themed by editing C below.
   ==================================================================== */

export const C = {
  ink: '#10132B',
  inkSoft: '#1A1E42',
  sidebarTop: '#12163B',
  sidebarBottom: '#0B0E24',
  signal: '#4C6FFF',
  amber: '#F5A524',
  emerald: '#16A34A',
  violet: '#7C5CFC',
  rose: '#E11D48',
  pageBg: '#F5F6FA',
  card: '#FFFFFF',
  border: '#E8EAF1',
  textMuted: '#64748B',
  textFaint: '#94A3B8',
};

export const FONT_UI = "'Inter', system-ui, -apple-system, sans-serif";
export const FONT_DATA = "'Roboto Mono', ui-monospace, SFMono-Regular, monospace";

/* ---------------------------------------------------------------- */
/*  Formatting helpers — every number the panel renders goes through */
/*  one of these so grouping/currency stays consistent page to page. */
/* ---------------------------------------------------------------- */

export const fmtINR = (n, { decimals = 0 } = {}) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;

export const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

export const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return `${fmtDate(date)}, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

// "Today, 11:42 AM" / "Yesterday, 6:02 PM" / "12 Aug 2026, 09:15 AM"
export const fmtRelativeStamp = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  return `${fmtDate(date)}, ${time}`;
};

export const daysUntil = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
};

/* ---------------------------------------------------------------- */
/*  Primitives                                                       */
/* ---------------------------------------------------------------- */

export function Mono({ children, className = '', style = {} }) {
  return (
    <span className={className} style={{ fontFamily: FONT_DATA, ...style }}>
      {children}
    </span>
  );
}

const PILL_TONES = {
  neutral: { bg: '#F1F5F9', fg: '#475569' },
  success: { bg: '#ECFDF5', fg: '#047857' },
  warning: { bg: '#FFFBEB', fg: '#B45309' },
  danger: { bg: '#FEF2F2', fg: '#B91C1C' },
  info: { bg: '#EFF3FF', fg: '#3350D6' },
  purple: { bg: '#F5F2FF', fg: '#6D3EF0' },
};

export function Pill({ tone = 'neutral', children, className = '' }) {
  const t = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${className}`}
      style={{ background: t.bg, color: t.fg }}
    >
      {children}
    </span>
  );
}

export function Card({ children, className = '', style = {}, ...rest }) {
  return (
    <div
      className={`rounded-xl ${className}`}
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        boxShadow: '0 1px 2px rgba(16,19,43,0.04)',
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-[26px] leading-tight font-bold" style={{ color: C.ink, fontFamily: FONT_UI }}>
          {title}
        </h1>
        {subtitle && (
          <p className="text-[13px] mt-1" style={{ color: C.textMuted }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

export function PrimaryButton({ children, icon: Icon, className = '', style = {}, ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 ${className}`}
      style={{ background: C.ink, ...style }}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

export function GhostButton({ children, icon: Icon, className = '', style = {}, ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-slate-50 disabled:opacity-50 ${className}`}
      style={{ background: C.card, border: `1px solid ${C.border}`, color: C.ink, ...style }}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

// Small square icon action, as used in the client-card footer and table rows.
export function IconButton({ icon: Icon, title, tone = C.textMuted, className = '', ...props }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`p-2 rounded-lg transition-colors hover:bg-slate-100 disabled:opacity-40 ${className}`}
      {...props}
    >
      <Icon className="w-4 h-4" style={{ color: tone }} />
    </button>
  );
}

/* ---------------------------------------------------------------- */
/*  Charts — hand-rolled SVG (the project ships no chart library)     */
/* ---------------------------------------------------------------- */

export function Sparkline({ data = [], color = C.signal, height = 34 }) {
  const pts = data.length ? data : [0, 0];
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const span = max - min || 1;
  const w = 100;
  const path = pts
    .map((v, i) => {
      const x = (i / Math.max(pts.length - 1, 1)) * w;
      const y = height - 4 - ((v - min) / span) * (height - 8);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Donut used by Expenditures "By category".
export function Donut({ data = [], size = 190, thickness = 26 }) {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  if (!total) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EEF1F6" strokeWidth={thickness} />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {data.map((d, i) => {
          const frac = (Number(d.value) || 0) / total;
          const len = frac * circumference;
          const seg = (
            <circle
              key={d.name || i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${circumference - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return seg;
        })}
      </g>
    </svg>
  );
}

// Grouped vertical bars (Profits: revenue / expenditure / profit per month).
export function GroupedBars({ groups = [], series = [], height = 210, formatValue = fmtINR }) {
  const all = groups.flatMap((g) => series.map((s) => Number(g[s.key]) || 0));
  const max = Math.max(...all, 1);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => max * f);

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex gap-3" style={{ minWidth: Math.max(groups.length * 96, 320) }}>
        <div className="flex flex-col justify-between text-right pr-1" style={{ height, color: C.textFaint }}>
          {[...ticks].reverse().map((t, i) => (
            <Mono key={i} className="text-[10px]">
              {formatValue(t)}
            </Mono>
          ))}
        </div>
        <div className="flex-1">
          <div className="flex items-end gap-4" style={{ height, borderBottom: `1px solid ${C.border}` }}>
            {groups.map((g) => (
              <div key={g.label} className="flex-1 flex items-end justify-center gap-1.5" style={{ height }}>
                {series.map((s) => {
                  const v = Number(g[s.key]) || 0;
                  return (
                    <div
                      key={s.key}
                      title={`${g.label} · ${s.label}: ${formatValue(v)}`}
                      className="rounded-t-[3px] w-full transition-all"
                      style={{ height: `${Math.max((v / max) * 100, 1)}%`, background: s.color, maxWidth: 26 }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-2">
            {groups.map((g) => (
              <div key={g.label} className="flex-1 text-center">
                <Mono className="text-[11px]" style={{ color: C.textMuted }}>
                  {g.label}
                </Mono>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-3">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-[11px]" style={{ color: C.textMuted }}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/*  Composites                                                       */
/* ---------------------------------------------------------------- */

// Stat tile with optional trend line + delta, as on Overview.
export function StatCard({ label, value, delta, deltaTone = 'up', spark, sparkColor = C.signal, hint }) {
  const tone = deltaTone === 'down' ? C.rose : deltaTone === 'flat' ? C.textMuted : C.emerald;
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[12px]" style={{ color: C.textMuted }}>
          {label}
        </p>
        {delta != null && (
          <Mono className="text-[11px] font-medium" style={{ color: tone }}>
            {delta}
          </Mono>
        )}
      </div>
      <Mono className="block text-[26px] font-semibold mt-1.5" style={{ color: C.ink }}>
        {value}
      </Mono>
      {hint && (
        <p className="text-[11px] mt-0.5" style={{ color: C.textFaint }}>
          {hint}
        </p>
      )}
      {spark && (
        <div className="mt-2">
          <Sparkline data={spark} color={sparkColor} />
        </div>
      )}
    </Card>
  );
}

// Card with a title bar and optional right-hand action link.
export function SectionCard({ title, action, children, bodyClassName = '', className = '' }) {
  return (
    <Card className={`overflow-hidden ${className}`}>
      {(title || action) && (
        <div
          className="flex items-center justify-between px-5 py-3.5"
          style={{ borderBottom: `1px solid ${C.border}` }}
        >
          <h3 className="text-[13px] font-semibold" style={{ color: C.ink }}>
            {title}
          </h3>
          {action}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </Card>
  );
}

// The amber "Needs your attention" strip on Overview.
export function AttentionBanner({ items = [], icon: Icon }) {
  if (!items.length) return null;
  return (
    <Card className="overflow-hidden" style={{ borderLeft: `3px solid ${C.amber}` }}>
      <div className="px-5 pt-3.5 pb-2 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" style={{ color: C.amber }} />}
        <span className="text-[12px] font-semibold" style={{ color: C.ink }}>
          Needs your attention
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x" style={{ borderColor: C.border }}>
        {items.map((it, i) => (
          <button
            key={i}
            type="button"
            onClick={it.onClick}
            disabled={!it.onClick}
            className="text-left px-5 py-3 transition-colors hover:bg-slate-50 disabled:hover:bg-transparent disabled:cursor-default"
          >
            <p className="text-[13px] font-semibold" style={{ color: C.ink }}>
              {it.title}
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: C.textMuted }}>
              {it.detail}
            </p>
          </button>
        ))}
      </div>
    </Card>
  );
}

// Search + filter toolbar sitting above a list (Clients).
export function Toolbar({ value, onChange, placeholder = 'Search…', children }) {
  return (
    <Card className="p-2.5 mb-5">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke={C.textFaint}
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm outline-none"
            style={{ background: C.card, border: `1px solid ${C.border}`, color: C.ink }}
          />
        </div>
        {children}
      </div>
    </Card>
  );
}

// Segmented control (Profits: Monthly / Half-Yearly / Yearly).
export function SegmentedControl({ options = [], value, onChange }) {
  return (
    <div className="inline-flex p-1 rounded-lg gap-1" style={{ background: '#EEF1F8' }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className="px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors"
            style={{
              background: active ? C.card : 'transparent',
              color: active ? C.signal : C.textMuted,
              border: active ? `1px solid ${C.signal}33` : '1px solid transparent',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Horizontal chip row (Expenditures month picker).
export function ChipTabs({ options = [], value, onChange }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className="px-3.5 py-1.5 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors"
            style={{
              background: active ? '#EEF1FF' : C.card,
              color: active ? C.signal : C.textMuted,
              border: `1px solid ${active ? `${C.signal}55` : C.border}`,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* Table primitives — uppercase tracked headers, mono data cells. */

export function Table({ children, minWidth = 860 }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

export function TH({ children, align = 'left', className = '' }) {
  return (
    <th
      className={`px-5 py-3 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${className}`}
      style={{ color: C.textFaint, textAlign: align, borderBottom: `1px solid ${C.border}` }}
    >
      {children}
    </th>
  );
}

export function TD({ children, align = 'left', className = '', style = {} }) {
  return (
    <td
      className={`px-5 py-3.5 text-[13px] align-middle ${className}`}
      style={{ color: C.ink, textAlign: align, borderBottom: `1px solid ${C.border}`, ...style }}
    >
      {children}
    </td>
  );
}

export function EmptyState({ icon: Icon, title, message, action }) {
  return (
    <div className="py-16 text-center">
      {Icon && <Icon className="w-10 h-10 mx-auto mb-3" style={{ color: '#CBD5E1' }} />}
      <p className="text-[14px] font-semibold" style={{ color: C.ink }}>
        {title}
      </p>
      {message && (
        <p className="text-[12px] mt-1" style={{ color: C.textMuted }}>
          {message}
        </p>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

// Avatar square with the org's initial, or its logo when one is uploaded.
export function OrgAvatar({ name, logoUrl, size = 40, radius = 12 }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name || 'Organization'}
        className="object-cover flex-shrink-0"
        style={{ width: size, height: size, borderRadius: radius, border: `1px solid ${C.border}` }}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center flex-shrink-0 text-white font-semibold"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: C.ink,
        fontSize: size * 0.42,
        fontFamily: FONT_UI,
      }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

// Toast-style inline feedback used at the top of pages.
export function Banner({ tone = 'success', message, onClose }) {
  if (!message) return null;
  const map = {
    success: { bg: '#ECFDF5', fg: '#047857', bd: '#A7F3D0' },
    error: { bg: '#FEF2F2', fg: '#B91C1C', bd: '#FECACA' },
    info: { bg: '#EFF3FF', fg: '#3350D6', bd: '#C7D2FE' },
  };
  const t = map[tone] || map.info;
  return (
    <div
      className="flex items-start justify-between gap-3 px-4 py-3 rounded-lg mb-4 text-[13px]"
      style={{ background: t.bg, color: t.fg, border: `1px solid ${t.bd}` }}
    >
      <span>{message}</span>
      {onClose && (
        <button type="button" onClick={onClose} className="font-semibold leading-none" aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}
